// DocumentPane — the paper, beside the reading.
//
// Every AI review lane proposes a reading of a document, and none of them is the
// record: the document is. So the pane is not a preview, it is one of the
// controls the reader lanes' threat model names, and a lane that cannot show the
// paper has to SAY so rather than render an empty box.
//
// Hence three states, all designed: loading, ready, and could-not-be-fetched.
// One sentence covering the last two would teach a reviewer to keep waiting for
// a document that is never coming.
//
// The lane's own warning about confirming without the paper rides on
// `emptyCopy`, printed under BOTH non-ready states, because that is exactly when
// it matters.

import React, { useEffect, useState } from 'react';
import { EmptyState } from '../ui/EmptyState';
import FadeIn from '../ui/FadeIn';

/**
 * Ready carries its URL and the blob's own type, so a render can never see
 * 'ready' with nothing to show. One state, one render per transition. The mime
 * that picks the branch is resolved at render, not stored: see below.
 */
type PaneState =
  | { status: 'loading' }
  | { status: 'ready'; url: string; blobType: string }
  | { status: 'error' };

export interface DocumentPaneProps {
  /** The authed fetch. The pane owns the object URL from here on. */
  fetchBlob: () => Promise<Blob>;
  /** Refetch when this changes: the document's id, normally. */
  refreshKey: string | number;
  /** The record's stored mime. `blob.type` is the fallback. */
  mimeType?: string | null;
  /** The lane's sentence, shown under BOTH non-document states. */
  emptyCopy: React.ReactNode;
  /** Appended to the frame. */
  className?: string;
  /**
   * Fill the height the caller gives the frame instead of standing at the
   * pane's own minimum.
   *
   * The default is a tall-enough box that grows with the page. `fill` is for a
   * caller that has already sized the frame — a sticky column pinned to the
   * viewport, say — where the intrinsic minimums fight that size instead of
   * respecting it: a 620px floor under a short viewport makes the pane
   * overflow the very column that was meant to contain it.
   */
  fill?: boolean;
}

export const DocumentPane: React.FC<DocumentPaneProps> = ({
  fetchBlob, refreshKey, mimeType, emptyCopy, className, fill = false,
}) => {
  const [state, setState] = useState<PaneState>({ status: 'loading' });

  // The document comes through a gated proxy, so it arrives as a blob and is
  // shown as an object URL: a bare iframe or img src cannot carry the Bearer
  // token. Revoked on the way out, because blob URLs leak until they are.
  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    setState({ status: 'loading' });
    fetchBlob()
      .then((blob) => {
        const created = URL.createObjectURL(blob);
        // A refreshKey that flipped mid-flight already ran the cleanup below,
        // so this URL has nobody to revoke it. Do it here and render nothing.
        if (cancelled) {
          URL.revokeObjectURL(created);
          return;
        }
        url = created;
        setState({ status: 'ready', url: created, blobType: blob.type });
      })
      .catch(() => { if (!cancelled) setState({ status: 'error' }); });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // The record's stored mime wins; the blob's own type is the fallback for a
  // lane whose records can lack one. Resolved HERE rather than frozen into
  // state at fetch time: a lane that renders while its record is still loading
  // passes mimeType late, and a stored resolution would never see it.
  const mime = state.status === 'ready' ? mimeType || state.blobType : '';

  // The frame belongs to the pane: the non-document states center with m-auto,
  // which only works because this container is flex.
  return (
    <div className={`bg-black/40 border border-gray-800 rounded-lg flex items-stretch overflow-hidden ${fill ? 'h-full' : 'min-h-[480px]'}${className ? ` ${className}` : ''}`}>
      <FadeIn key={state.status} className="flex w-full min-h-0 items-stretch">
      {state.status === 'ready' ? (
        mime === 'application/pdf' ? (
          <iframe
            title="Original document"
            src={state.url}
            className={`w-full rounded-lg ${fill ? 'h-full' : 'min-h-[620px]'}`}
          />
        ) : (
          // Filling, an image is contained rather than left at natural size:
          // a phone photo of an invoice is taller than any viewport, and a
          // pane that scrolls its own picture inside a pinned column is a
          // worse way to read paper than one that fits it.
          <img
            src={state.url}
            alt="Original document"
            className={`rounded-lg ${fill ? 'max-h-full max-w-full m-auto object-contain' : 'max-w-full self-start'}`}
          />
        )
      ) : (
        <EmptyState variant="area" className="m-auto">
          {state.status === 'loading'
            ? 'The document is loading.'
            : 'The document could not be fetched. Reload the page to try it again.'}{' '}
          {emptyCopy}
        </EmptyState>
      )}
      </FadeIn>
    </div>
  );
};

export default DocumentPane;
